import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs/operators';

import { InteractionScoringEntity } from '../../coaching-base/config';
import { FocusDto, NoteCategory } from '../../coaching-base/general';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { ScoreFormValue } from '../observation-score/score-form-value.model';

@Component({
    selector: '[cmpsObservationScores]',
    templateUrl: './scores.observation.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ScoresObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ScoresObservationComponent),
            multi: true,
        },
    ],
    host: {
        'data-e2e': 'scores-observation',
    },
})
@TakeUntilDestroy()
export class ScoresObservationComponent
    implements OnInit, ControlValueAccessor, Validator, TakeUntilDestroy
{
    /** @private */ _i18n = TranslationKeyMap;
    @Input() config: InteractionScoringEntity;
    @Input() focus: FocusDto;
    @Input() defaultNoteCategory: NoteCategory;
    @Input() published: boolean;
    /** @private */ _form: FormArray;
    private _blackout: boolean;
    private _onChange: (_: ScoreFormValue[]) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;

    public showScoring = false;
    public _cacheFormValue: ScoreFormValue[] = [];

    /** @private */ get _value(): ScoreFormValue[] {
        return this._form.value;
    }

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnInit(): void {
        this._form = new FormArray([]);

        this._form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                tap((value1) => {
                    CMPS_APP_DEF.ENV !== 'prod' &&
                        console.log('_onChange scores');
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(value1);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._form && (this._form as any).clear2();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.disable({ emitEvent: false })
            : this._form.enable({ emitEvent: false });
    }

    writeValue(obj: ScoreFormValue[]): void {
        this._patchForm(obj);
        this._cacheFormValue = [...obj];
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        return this._form.errors;
    }

    private _patchForm(value: ScoreFormValue[]): void {
        this._blackout = true;

        (this._form as any).clear2();
        (this._form as any).push2(
            ...(value || []).map((e) => this._formBuilder.control(e)),
        );

        this.showScoring = this.focus && this._value && this._value.length > 0;

        this._blackout = false;
    }

    confirmReset(e: MouseEvent): void {
        e.stopPropagation();

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9666_ARE_YOU_SURE_YOU_WANT_TO_RESET_EVALUATION_TO_PREVIOUS_VALUE_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        // use loaded/default values
                        // reset form to trigger changes
                        this._form.reset(this._cacheFormValue);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
